import type {Filter} from '@/modules/common/models/filters';

import {useBreakpointDown} from '@travel-frontend/core';
import {Skeleton} from '@travel-frontend/ui-components';
import {isEqual, noop} from 'lodash';
import {memo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {KeywordTypes} from '@/api/keywordApi';
import {PopularFilters} from '@/components/PopularFilters';
import {ResultListHeader} from '@/components/ResultListHeader';
import {ResultListHeaderPlaceholder} from '@/components/ResultListHeaderPlaceholder';
import {RegionSelect} from '@/modules/common/components/RegionSelect';
import {SortingDropdown} from '@/modules/common/components/SortingDropdown';
import {useFilterOverlay} from '@/modules/common/context/FilterOverlay';
import {useSeaCampaign} from '@/modules/common/context/SeaCampaign';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {useDestinationFilter} from '@/modules/common/hooks/useDestinationFilter';
import {usePopularFilters} from '@/modules/common/hooks/usePopularFilters';
import {useFilters} from '@/modules/hotels/hooks/useFilters';
import {useAppSelector} from '@/redux/config';
import {
  EVENT_FILTER_REMOVE,
  EVENT_FILTER_SET,
} from '@/redux/middleware/googleTagManager';
import {
  areHotelsLoaded as getAreHotelsLoaded,
  haveHotelsFailedLoading as getHaveHotelsFailedLoading,
  getHotelCountFiltered,
  getRegions,
  selectAreHotelsLoading,
} from '@/redux/v1/hotels/list/reducer';
import {testId} from '@/utils/testId';

type Props = {
  activeFilterLabels: Array<string>;
  sortingControlEnabled?: boolean;
  onChange?: () => void;
};

export const Header: React.FunctionComponent<Props> = memo(
  function Header({
    activeFilterLabels,
    onChange = noop,
    sortingControlEnabled,
  }) {
    const dispatch = useDispatch();
    const {t} = useTranslation('hotels');
    const smDown = useBreakpointDown('sm');
    const mdDown = useBreakpointDown('md');

    const areHotelsLoading = useSelector(selectAreHotelsLoading);
    const areHotelsLoaded = useAppSelector(getAreHotelsLoaded);
    const haveHotelsFailedLoading = useAppSelector(getHaveHotelsFailedLoading);
    const hotelCountFiltered = useAppSelector(getHotelCountFiltered);
    const regions = useSelector(getRegions);

    const seaCampaign = useSeaCampaign();
    const destination = useDestinationFilter();
    const {filters, isReady: filtersReady} = useFilters();

    const {toggleFilterOverlay} = useFilterOverlay();
    const currentPage = useCurrentPage();

    const popularFilterItems = usePopularFilters(filters, currentPage);
    const handleChange =
      (filter: Filter, popular?: boolean) =>
      (event: {
        type: 'clear' | 'pop' | 'set' | 'push';
        value: null | number | string | Array<string>;
      }) => {
        if (event.type === 'clear' || event.type === 'pop') {
          dispatch({
            type: EVENT_FILTER_REMOVE,
            payload: {
              filters: [[filter.id, event.value]],
              popular,
              currentPage,
            },
          });
        } else if (event.type === 'set' || event.type === 'push') {
          dispatch({
            type: EVENT_FILTER_SET,
            payload: {
              filters: [[filter.id, event.value]],
              popular,
              currentPage,
            },
          });
        }
        // @ts-expect-error ...
        filter.setValue(event.value);
        onChange();
      };

    if (!areHotelsLoaded && haveHotelsFailedLoading) {
      return null;
    }

    if (!areHotelsLoaded) {
      return <ResultListHeaderPlaceholder />;
    }

    const destinationName =
      destination.type === KeywordTypes.hotel
        ? destination.parentKeyword?.keyword || ''
        : destination?.keyword?.keyword || '';

    const subtitle =
      seaCampaign.active && mdDown && activeFilterLabels.length > 0
        ? activeFilterLabels.slice(0, 3).join(', ')
        : '';

    let regionSelectElement = undefined;
    if (regions.length > 0) {
      regionSelectElement = areHotelsLoading ? (
        <Skeleton variant="rect" height="40px" width="200px" />
      ) : (
        <RegionSelect />
      );
    }

    const translationKey =
      destination.type === KeywordTypes.hotel
        ? 'headline_alternative'
        : 'headline';

    return (
      <ResultListHeader
        title={
          areHotelsLoading ? (
            <Skeleton variant="rect" height="40px" />
          ) : (
            t(translationKey, {
              count: hotelCountFiltered,
              countFormatted: hotelCountFiltered,
              keyword: destinationName,
              context: destinationName ? 'withKeyword' : 'withoutKeyword',
            })
          )
        }
        subtitle={subtitle}
        filterControl={
          smDown && (
            <PopularFilters
              data-testid={testId(`filter-popular`)}
              items={filtersReady ? popularFilterItems : []}
              onClickFilterTrigger={toggleFilterOverlay}
              isSmallView
              // @ts-expect-error ...
              handleChange={handleChange}
            />
          )
        }
        sortingControl={
          sortingControlEnabled && <SortingDropdown isCentered={mdDown} />
        }
        regionSelect={regionSelectElement}
        data-testid={testId('headline')}
      />
    );
  },
  // @FIXME workaround because memoizing "activeFilterLabels" up the tree is difficult right now
  isEqual
);
